import type { IconName } from '../../ui';
import type { SettingsResponse } from '../../hooks/useGetSettings';
import { isInvoicingOrgSession, type SessionContextType } from '../../contexts/SessionContext';
import { canAddOrganizationLocation } from '../../utils/yaxleAdminAccess';
import { isInternalStaff } from '../../utils/internalAccess';

export type SettingsGroupId = 'personal' | 'general' | 'partners' | 'purchasing' | 'invoicing' | 'beta' | 'yaxle';

export interface SettingsGroup {
    id: SettingsGroupId;
    label: string;
}

/** Sidebar group order. */
export const SETTINGS_GROUPS: SettingsGroup[] = [
    { id: 'personal', label: 'Personal' },
    { id: 'general', label: 'General' },
    { id: 'partners', label: 'Partners' },
    { id: 'purchasing', label: 'Purchasing' },
    { id: 'invoicing', label: 'Invoicing' },
    { id: 'beta', label: 'Beta' },
    { id: 'yaxle', label: 'Yaxle' },
];

/** Everything a visibility rule is allowed to look at. */
export interface SettingsAccessContext {
    session: SessionContextType | undefined;
    settings: SettingsResponse | undefined;
    /** Keys the API actually returned, across both section groups. */
    apiSectionKeys: Set<string>;
}

export interface SettingsNavEntry {
    /** URL segment: /settings/<id>. Stable — changing it breaks saved links. */
    id: string;
    label: string;
    /** Shown on the overview card and as the page description. */
    description: string;
    /** Overrides `description` for the current viewer when it returns a string. */
    getDescription?: (ctx: SettingsAccessContext) => string | undefined;
    group: SettingsGroupId;
    icon: IconName;
    /**
     * The API section key this entry needs. When set and the key is missing
     * from the settings response, the entry is hidden. Client-only sections
     * (Profile, Notifications, Inventory) omit it.
     */
    apiSectionKey?: string;
    /** Extra visibility rule layered on top of apiSectionKey. */
    isVisible?: (ctx: SettingsAccessContext) => boolean;
    /** `table` runs the section edge to edge. Defaults to `form`. */
    pageVariant?: 'form' | 'table';
    /** Small label beside the page title, e.g. "Optional". */
    badge?: string;
    /** Extra terms the search bar matches, beyond label and description. */
    keywords: string[];
}

const isAdmin = (ctx: SettingsAccessContext) =>
    ctx.session?.isAuthenticated === true && ctx.session.isAdmin === true;

/** Invoicing settings pages are editable only with preferences. Workflow readers still get the data. */
const canEditInvoicingSettings = (ctx: SettingsAccessContext) =>
    ctx.session?.isAuthenticated === true && ctx.session.permissions.includes('preferences');

/**
 * Yaxle staff (employee accounts or company-domain emails). Beta switches turn
 * on features that aren't finished, so we flip them for an org rather than
 * letting the org opt itself in. Display gate only — the API is the real guard
 * on the write.
 */
const isPartBayStaff = (ctx: SettingsAccessContext) => isInternalStaff(ctx.session);

/**
 * Single source of truth for the Settings area. The sidebar, the overview
 * cards, search, and routing are all derived from this list, so adding a
 * section means adding one entry here plus one component.
 */
export const SETTINGS_NAV: SettingsNavEntry[] = [
    // Personal ---------------------------------------------------------------
    {
        id: 'profile',
        label: 'Profile',
        description: 'Your name, contact details, and locations.',
        group: 'personal',
        icon: 'user',
        keywords: ['personal information', 'name', 'email', 'phone', 'avatar', 'my locations', 'password', 'security'],
    },
    {
        id: 'notifications',
        label: 'Notifications',
        description: 'Choose which alerts you receive and where.',
        group: 'personal',
        icon: 'bell',
        // Inventory-only orgs, invoicing users on a full org, and technicians have no
        // rows here. Invoicing-only orgs do: flagged invoices, negotiation replies,
        // and invoice comments.
        isVisible: (ctx) =>
            ctx.session?.isAuthenticated === true &&
            (isInvoicingOrgSession(ctx.session) ||
                (ctx.session.organizationMode !== 'inventory' &&
                    ctx.session.position !== 'invoicing' &&
                    ctx.session.position !== 'technician')),
        pageVariant: 'table',
        keywords: ['emails', 'sms', 'text', 'alerts', 'quotes back', 'flagged parts', 'preferences'],
    },

    // General ----------------------------------------------------------------
    {
        id: 'users',
        label: 'Users',
        description: 'Invite teammates and manage their access.',
        group: 'general',
        icon: 'users',
        apiSectionKey: 'user',
        pageVariant: 'table',
        keywords: ['invite', 'teammates', 'deactivate', 'position', 'role', 'admin'],
    },
    {
        id: 'permissions',
        label: 'Permissions',
        description: 'Default tabs and permissions for each role.',
        getDescription: (ctx) =>
            isInvoicingOrgSession(ctx.session)
                ? 'Default tabs and permissions for invoicing users. Admins have the full set.'
                : undefined,
        group: 'general',
        icon: 'shieldLock',
        apiSectionKey: 'user',
        isVisible: isAdmin,
        pageVariant: 'table',
        keywords: ['default permissions', 'roles', 'tabs', 'access', 'executive', 'gm', 'central parts', 'parts manager', 'estimator', 'change selections', 'unflagged'],
    },
    {
        id: 'locations',
        label: 'Locations',
        description: "All locations loaded into our system. To add a location you'll need to contact us.",
        getDescription: (ctx) =>
            canAddOrganizationLocation(ctx.session) ? 'Add and manage shop locations.' : undefined,
        group: 'general',
        icon: 'mapPin',
        apiSectionKey: 'location',
        pageVariant: 'table',
        keywords: ['shops', 'sites', 'branches', 'addresses', 'stores', 'add location'],
    },
    {
        id: 'connections',
        label: 'Connections',
        description: 'Estimating, marketplace, and management system credentials.',
        getDescription: (ctx) =>
            isInvoicingOrgSession(ctx.session)
                ? 'Nexsyis connection for this organization.'
                : undefined,
        group: 'general',
        icon: 'plugConnected',
        apiSectionKey: 'integrations',
        keywords: [
            'integrations',
            'credentials',
            'estimating',
            'marketplaces',
            'management systems',
            'mitchell',
            'partstrader',
            'opstrax',
            'ops trax',
            'collisionlink',
            'nexsyis',
            'nexpart',
            'api keys',
        ],
    },
    {
        id: 'desktop-app',
        label: 'Desktop App',
        description: 'Download the app and manage server accounts.',
        group: 'general',
        icon: 'laptop',
        apiSectionKey: 'it-setup',
        keywords: ['it setup', 'download', 'installer', 'server accounts', 'inbound email', 'agent'],
    },
    {
        id: 'subscription',
        label: 'Subscription',
        description: 'Plans, payment method, and billing history.',
        group: 'general',
        icon: 'creditCard',
        apiSectionKey: 'subscriptions',
        keywords: ['billing', 'plan', 'trial', 'stripe', 'invoice', 'payment', 'card'],
    },

    // Partners ---------------------------------------------------------------
    {
        id: 'vendors',
        label: 'Vendor',
        description: 'Set vendor preferences and contractual terms.',
        group: 'partners',
        icon: 'buildingStore',
        apiSectionKey: 'vendor',
        pageVariant: 'table',
        keywords: ['suppliers', 'preferred', 'avoid', 'required', 'vendors', 'direct ordering', 'opstrax', 'collisionlink'],
    },
    {
        id: 'drp',
        label: 'DRP',
        description: 'Insurance partner configurations for price matching, ordering thresholds, and more.',
        group: 'partners',
        icon: 'shieldCheck',
        apiSectionKey: 'insurance',
        pageVariant: 'table',
        keywords: ['insurance', 'insurer', 'price match', 'estimate', 'prohibited suppliers', 'thresholds', 'direct repair'],
    },
    {
        id: 'oem',
        label: 'OEM',
        description: 'Set your OEM certifications, margin thresholds, and rebate programs.',
        group: 'partners',
        icon: 'car',
        apiSectionKey: 'oem',
        pageVariant: 'table',
        keywords: ['makes', 'manufacturer', 'certifications', 'rebates', 'thresholds'],
    },

    // Purchasing -------------------------------------------------------------
    {
        id: 'ems',
        label: 'EMS',
        description: 'Automatic EMS exports and price matching.',
        group: 'purchasing',
        icon: 'fileExport',
        apiSectionKey: 'ems',
        keywords: ['auto send', 'export', 'price match', 'estimate management'],
    },
    {
        id: 'quoting',
        label: 'Quoting',
        description: 'Quote timing, requoting, and marketplace rules.',
        group: 'purchasing',
        icon: 'fileDollar',
        apiSectionKey: 'quoting',
        keywords: ['requote', 'skip quoting', 'expedited', 'marketplace', 'scheduled requests', 'timing', 'stale'],
    },
    {
        id: 'selection',
        label: 'Selection',
        description: 'Delivery timelines, consolidation, and price matching.',
        group: 'purchasing',
        icon: 'clipboardCheck',
        apiSectionKey: 'selection',
        keywords: ['delivery', 'consolidation', 'backorder', 'allowable', 'promo pricing', 'carrier'],
    },
    {
        id: 'notes',
        label: 'Notes',
        description: 'System generated notes and where they appear.',
        group: 'purchasing',
        icon: 'notes',
        apiSectionKey: 'selection',
        pageVariant: 'table',
        keywords: ['system generated notes', 'carrier', 'comments', 'messages'],
    },
    {
        id: 'part-types',
        label: 'Part Types',
        description: 'How OEM, aftermarket, and recycled parts are chosen.',
        group: 'purchasing',
        icon: 'category',
        apiSectionKey: 'selection',
        keywords: ['oem preferred', 'oem override', 'max margin', 'min price', 'aftermarket', 'recycled', 'reman', 'capa', 'wheels', 'older vehicles', 'lkq', 'flag lkq', 'price match to lkq'],
    },
    {
        id: 'part-allowances',
        label: 'Part Allowances',
        description: 'Allowed, forbidden, and flagged part rules.',
        group: 'purchasing',
        icon: 'listCheck',
        apiSectionKey: 'selection',
        pageVariant: 'table',
        keywords: ['allowed parts', 'forbidden parts', 'flagged parts', 'part rules', 'exclusions'],
    },
    {
        id: 'ordering',
        label: 'Ordering',
        description: 'Auto-ordering, margins, and supplier changes.',
        group: 'purchasing',
        icon: 'shoppingCart',
        apiSectionKey: 'ordering',
        keywords: ['auto order', 'margin', 'supplier change', 'ccc ordering', 'purchase order'],
    },
    {
        id: 'rekeying',
        label: 'Re-Keying',
        description: 'Re-key cutoffs, exclusions, and manual POs.',
        group: 'purchasing',
        icon: 'keyboard',
        apiSectionKey: 'rekey',
        keywords: ['rekey', 're-key', 'cutoff', 'apu', 'manual po', 'excluded makes', 'advisor score', 'price matched', 'capa', 'certification'],
    },
    {
        id: 'inventory',
        label: 'Inventory',
        description: 'Business lines, buy price method, and contacts.',
        group: 'purchasing',
        icon: 'warehouse',
        isVisible: (ctx) => ctx.settings?.inventoryEnabled === true && isAdmin(ctx),
        keywords: ['business lines', 'buy price', 'lifo', 'fifo', 'part fulfillment', 'stock'],
    },

    // Invoicing --------------------------------------------------------------
    {
        id: 'invoicing-general',
        label: 'General',
        description: 'Line item handling and Nexsyis reconciliation.',
        group: 'invoicing',
        icon: 'fileInvoice',
        apiSectionKey: 'invoicing',
        isVisible: canEditInvoicingSettings,
        keywords: ['freight', 'sales tax', 'nexsyis', 'reconciliation', 'unmatched', 'vendor mappings', 'invoice reference'],
    },
    {
        id: 'invoicing-email-forwarding',
        label: 'Email Forwarding',
        description: 'Invoice inbox addresses for forwarded invoices.',
        group: 'invoicing',
        icon: 'mailForward',
        apiSectionKey: 'invoicing',
        isVisible: canEditInvoicingSettings,
        keywords: ['inbox', 'forward', 'email address', 'upload by email'],
    },
    {
        id: 'invoicing-flags',
        label: 'Flags',
        description: 'What gets flagged for review, and at what threshold.',
        group: 'invoicing',
        icon: 'flag',
        apiSectionKey: 'invoicing',
        isVisible: canEditInvoicingSettings,
        keywords: ['mismatch', 'quantity', 'price', 'amount', 'thresholds', 'both directions', 'discount flag', 'vendor flagging', 'special vendors'],
    },
    {
        id: 'invoicing-taxes',
        label: 'Taxes',
        description: 'Default tax and PST rates by location.',
        // Nothing breaks without a rate configured, so the header says so.
        badge: 'Optional',
        group: 'invoicing',
        icon: 'receiptTax',
        apiSectionKey: 'invoicing',
        isVisible: canEditInvoicingSettings,
        keywords: ['tax rate', 'pst', 'gst', 'sales tax', 'location'],
    },
    {
        id: 'invoicing-markups',
        label: 'Markups',
        description: 'Parts-specific and sublet-specific markups.',
        group: 'invoicing',
        icon: 'trendingUp',
        apiSectionKey: 'invoicing',
        isVisible: canEditInvoicingSettings,
        pageVariant: 'table',
        keywords: ['lkq', 'used', 'aftermarket', 'sublet', 'markup', 'list price'],
    },
    {
        id: 'invoicing-discounts',
        label: 'Discounts',
        description: 'Parts-specific and sublet-specific discounts.',
        group: 'invoicing',
        icon: 'tag',
        apiSectionKey: 'invoicing',
        isVisible: canEditInvoicingSettings,
        pageVariant: 'table',
        keywords: ['lkq', 'used', 'aftermarket', 'sublet', 'discount'],
    },

    // Beta -------------------------------------------------------------------
    {
        id: 'beta',
        label: 'Beta Features',
        description: 'Turn on features that are still in beta for this organization.',
        group: 'beta',
        icon: 'flask',
        // Invoicing-only orgs have no beta features yet. The page stays hidden
        // until one exists, rather than showing parts-ordering switches.
        isVisible: (ctx) => isPartBayStaff(ctx) && !isInvoicingOrgSession(ctx.session),
        keywords: [
            'beta',
            'early access',
            'preview',
            'feature flags',
            'mark parts received',
            'receiving',
            'inventory',
            'nexsyis',
            'nex',
            'purchase orders',
        ],
    },

    // Yaxle -------------------------------------------------------------------
    {
        id: 'yaxle-team',
        label: 'Team',
        description: 'Invite Yaxle employees and choose what they can do in every organization.',
        group: 'yaxle',
        icon: 'users',
        // Not tied to this org's settings: team managers see it in every org they open.
        isVisible: (ctx) =>
            ctx.session?.isAuthenticated === true && ctx.session.canManageInternalTeam,
        pageVariant: 'table',
        keywords: ['employees', 'staff', 'internal', 'invite', 'permissions', 'access', 'team manager', 'microsoft 365', 'deactivate'],
    },
];

/**
 * Invoicing-only orgs don't run procurement. Partners, Purchasing, and Desktop
 * App stay hidden. Beta stays hidden until an invoicing beta exists. Yaxle
 * Team keeps its own staff gate.
 */
const INVOICING_ORG_NAV_IDS = new Set<string>([
    'profile',
    'notifications',
    'users',
    'permissions',
    'locations',
    'connections',
    'subscription',
    'invoicing-general',
    'invoicing-email-forwarding',
    'invoicing-flags',
    'invoicing-taxes',
    'invoicing-markups',
    'invoicing-discounts',
    'yaxle-team',
]);

/** Collect every section key the API returned, across both groups. */
export const collectApiSectionKeys = (settings: SettingsResponse | undefined): Set<string> => {
    const keys = new Set<string>();
    settings?.topSection?.sections.forEach((section) => keys.add(section.key));
    settings?.bottomSection?.sections.forEach((section) => keys.add(section.key));
    return keys;
};

export const isEntryVisible = (entry: SettingsNavEntry, ctx: SettingsAccessContext): boolean => {
    if (isInvoicingOrgSession(ctx.session) && !INVOICING_ORG_NAV_IDS.has(entry.id)) return false;
    if (entry.apiSectionKey && !ctx.apiSectionKeys.has(entry.apiSectionKey)) return false;
    if (entry.isVisible && !entry.isVisible(ctx)) return false;
    return true;
};

export const getEntryDescription = (entry: SettingsNavEntry, ctx: SettingsAccessContext): string =>
    entry.getDescription?.(ctx) ?? entry.description;

export const getVisibleNav = (ctx: SettingsAccessContext): SettingsNavEntry[] =>
    SETTINGS_NAV.filter((entry) => isEntryVisible(entry, ctx));

/** Visible entries bucketed by group, preserving group and entry order. */
export const getVisibleNavByGroup = (
    ctx: SettingsAccessContext,
): Array<{ group: SettingsGroup; entries: SettingsNavEntry[] }> => {
    const visible = getVisibleNav(ctx);
    return SETTINGS_GROUPS.map((group) => ({
        group,
        entries: visible.filter((entry) => entry.group === group.id),
    })).filter((bucket) => bucket.entries.length > 0);
};

/**
 * Full settings access unlocks the overview and the search bar. Users with only
 * `preferences` or `it-settings` keep the sections their permission grants but
 * land straight on a section instead.
 */
export const hasFullSettingsAccess = (session: SessionContextType | undefined): boolean =>
    session?.isAuthenticated === true && session.permissions.includes('settings');

/** Case-insensitive match across label, description, group, and keywords. */
export const matchesSearch = (
    entry: SettingsNavEntry,
    rawQuery: string,
    ctx?: SettingsAccessContext,
): boolean => {
    const query = rawQuery.trim().toLowerCase();
    if (!query) return true;
    const group = SETTINGS_GROUPS.find((g) => g.id === entry.group)?.label ?? '';
    const description = ctx ? getEntryDescription(entry, ctx) : entry.description;
    const haystack = [entry.label, description, entry.description, group, ...entry.keywords]
        .join(' ')
        .toLowerCase();
    return query.split(/\s+/).every((term) => haystack.includes(term));
};
